import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { forwardRef, ReactNode, CSSProperties, ElementType } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useUser } from 'app/state/contexts/UserContext';
import { UserCircle } from 'app/components';
import request from 'shared/lib/request';
import authStore from 'shared/auth/authStore';

import { IfHasPermission } from 'app/components';

interface CustomToggleProps {
  children?: ReactNode;
  onClick?: () => void;
}

const CustomToggle = forwardRef<HTMLButtonElement, CustomToggleProps>(({ children, onClick }, ref) => (
  <button className="d-flex align-items-center fs-3 fw-medium px-4 bg-transparent h-100 border-0 border-start border-end" ref={ref} onClick={onClick}>
    {children}
  </button>
));

interface CustomMenuProps {
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  'aria-labelledby'?: string;
}

interface MenuUser {
  firstName: string;
  lastName: string;
}

const CustomMenu = forwardRef<HTMLDivElement, CustomMenuProps>(({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
  return (
    <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
      <ul className="list-unstyled mb-0">{children}</ul>
    </div>
  );
});

export default function OrganizationMenu() {
  const { t } = useTranslation('menu');

  const { user, organizations } = useUser() as { user: MenuUser; organizations: unknown[] };

  const showAdminSection =
    authStore.hasRole('ROLE_ADMIN') ||
    authStore.hasPermission('LABELS_CREATE') ||
    authStore.hasPermission('LISTS_CREATE') ||
    authStore.hasPermission('TAXONOMY_IMPORT') ||
    organizations.length > 1;

  const handleClickLogout = () => {
    request.post('/auth/logout.do').then(() => {
      window.location.href = '/';
    });
  };

  return (
    <Dropdown>
      <Dropdown.Toggle as={CustomToggle as ElementType}>
        <UserCircle user={user} />
        <div className="ms-3">
          {user.firstName} {user.lastName}
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu className="border-gray-300 rounded-sm pt-3 min-w-100" as={CustomMenu as ElementType}>
        {showAdminSection ? (
          <>
            <div className="text-gray-700 fw-semibold fs-4 mx-3">{t('admin-header')}</div>
            <Dropdown.Item className="px-3 py-2" as={Link} to="/organizations">
              {t('organisations')}
            </Dropdown.Item>
            {authStore.hasRole('ROLE_ADMIN') ? (
              <Dropdown.Item className="px-3 py-2" as={Link} to="/admin/users">
                {t('admin.users')}
              </Dropdown.Item>
            ) : null}
            <IfHasPermission permission="LABELS_CREATE">
              <Dropdown.Item className="px-3 py-2" as={Link} to="/admin/labels">
                {t('admin.labels')}
              </Dropdown.Item>
            </IfHasPermission>
            <IfHasPermission permission="LISTS_CREATE">
              <Dropdown.Item className="px-3 py-2" as={Link} to="/admin/lists">
                {t('admin.lists')}
              </Dropdown.Item>
            </IfHasPermission>
            <IfHasPermission permission="TAXONOMY_IMPORT">
              <Dropdown.Item className="px-3 py-2" as={Link} to="/admin/taxonomies">
                {t('admin.taxonomies')}
              </Dropdown.Item>
            </IfHasPermission>
          </>
        ) : null}

        <div className="text-gray-700 fw-semibold fs-4 mx-3 mt-3">{t('profile-header')}</div>
        <Dropdown.Item className="px-3 py-2" as={Link} to="/myprofile">
          <i className="fa-regular fa-user me-3" />
          {t('my-profile')}
        </Dropdown.Item>
        <Dropdown.Item className="px-3 py-2" onClick={handleClickLogout}>
          <i className="fa-regular fa-right-from-bracket me-3" />
          {t('logout')}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
